import {Center, createStyles, SegmentedControl, Stack, Text, Box} from '@mantine/core';
import React from 'react';

import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import {PlateSchema, PlateSchemaType, PlateSize} from 'samples/storables/StorableSchemaConstants';
import getPresetPlateSizes from 'settings/samples/getPresetPlateSizes';
import {FONT_PROXIMA_TITLE, FONT_PROXIMA_12} from 'styles/FontConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Spinner from 'ui-library/Spinner';

import PlateDesignTemplateSelector from './PlateDesignTemplateSelector';

export enum CreationMode {
  SCRATCH = 'scratch',
  TEMPLATE = 'template',
}

export interface DefineScratchPlateSettingsStepProps {
  name: string | null;
  onChangeName: (name: string) => void;
  plateSize: PlateSize | null;
  onChangePlateSize: (plateSize: PlateSize) => void;
  plateSchema: PlateSchema | null;
  onChangePlateSchema: (schema: PlateSchema | null) => void;
  creationMode?: CreationMode;
  onChangeCreationMode?: (mode: CreationMode) => void;
  selectedTemplateId?: string | null;
  onSelectTemplate?: (templateId: string, scratchPlateId: string) => void;
  /**
   * If creating a template, the selected template collection.
   */
  selectedTemplateCollectionId?: string | null;
  /**
   * If creating a template, callback to to show the template selector.
   *
   * If passed, a template selector is show; if not passed, the selector is hidden..
   */
  onSelectTemplateCollection?: (templateCollectionId: string) => void;
  isTemplateLoading?: boolean;
}

function getSizeName(height: number, width: number): string {
  return `${height * width} (${height} x ${width})`;
}

function PlateSchemaSizeSelector({
  plateSize,
  onChangePlateSize,
}: Pick<DefineScratchPlateSettingsStepProps, 'plateSize' | 'onChangePlateSize'>): JSX.Element {
  const plateSchemaSizeOptions = getPresetPlateSizes().map(([height, width]) => ({
    name: getSizeName(height, width),
    size: [height, width] as PlateSize,
  }));

  return (
    <SelectDropdown
      dataTestAttrs={{component: 'PlateSchemaSizeSelector', element: 'input'}}
      displayName={plateSize != null ? getSizeName(plateSize[0], plateSize[1]) : 'Select size...'}
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
          {plateSchemaSizeOptions.map((option) => (
            <Option
              key={option.name}
              onSelect={() => {
                onChangePlateSize(option.size);
              }}
              value={option}
            >
              {option.name}
            </Option>
          ))}
        </Menu>
      }
    />
  );
}

export default function DefineScratchPlateSettingsStep({
  name,
  onChangeName,
  plateSize,
  onChangePlateSize,
  plateSchema,
  onChangePlateSchema,
  creationMode,
  onChangeCreationMode,
  selectedTemplateId,
  onSelectTemplate,
  selectedTemplateCollectionId,
  onSelectTemplateCollection,
  isTemplateLoading,
}: DefineScratchPlateSettingsStepProps): JSX.Element {
  const {classes} = useStyles();
  const plateSchemaSelectorDisabledMessage = plateSize == null ? 'Select a plate size first' : undefined;

  const plateSchemaFilter = (schema: PlateSchema): boolean => {
    if (plateSize == null) {
      return false;
    }
    return schema.height === plateSize[0] && schema.width === plateSize[1];
  };

  const showModeToggle = onChangeCreationMode != null;

  return (
    <Stack spacing="xs" maw={1040}>
      {/* Stack for the toggle between creation method */}
      {showModeToggle && (
        <Stack spacing="xs">
          <FieldLabel label="Creation method" labelStyle={FieldLabel.STYLES.LABEL} />
          <SegmentedControl
            aria-label="Creation method"
            classNames={{label: classes.segmentLabel, control: classes.segmentControl}}
            data={[
              {
                label: (
                  <Text className={classes.segmentLabelText}>
                    <Text className={classes.segmentLabelTitle}>Create from scratch</Text>
                    Manually define the name, size, well roles, and concentration layers of your plate design.
                  </Text>
                ),
                value: CreationMode.SCRATCH,
              },
              {
                label: (
                  <Text className={classes.segmentLabelText}>
                    <Text className={classes.segmentLabelTitle}>Create from template</Text>
                    Use an existing template to define your plate design. Updates can be made before the
                    design is created.
                  </Text>
                ),
                value: CreationMode.TEMPLATE,
              },
            ]}
            value={creationMode ?? CreationMode.SCRATCH}
            onChange={(value) => {
              onChangeCreationMode(value as CreationMode);
            }}
          />
        </Stack>
      )}
      {/* Stack for all inputs, conditionally shown based on the creation mode and template selection */}
      <Stack spacing="xs" mt="md">
        {creationMode === CreationMode.TEMPLATE && onSelectTemplate != null ? (
          // mb is specifically defined as 15px to match the FormGroups used below
          <Box mb="15px">
            <FieldLabel
              isRequired={true}
              label="Plate design template "
              labelStyle={FieldLabel.STYLES.LABEL}
            />
            <PlateDesignTemplateSelector
              selectedTemplateId={selectedTemplateId ?? null}
              onSelectTemplate={onSelectTemplate}
            />
          </Box>
        ) : null}
        {isTemplateLoading && (
          <Center>
            <Spinner />
          </Center>
        )}
        {!isTemplateLoading && !(creationMode === CreationMode.TEMPLATE && selectedTemplateId == null) && (
          <>
            <FormGroup>
              <TextField
                isRequired={true}
                label="Name"
                autoFocus={creationMode !== CreationMode.TEMPLATE}
                placeholder="Plate design name..."
                value={name ?? ''}
                onChange={(e) => {
                  onChangeName(e.target.value);
                }}
                dataTestAttrs={{component: 'DefineScratchPlateSettingsStep', element: 'nameInput'}}
              />
            </FormGroup>
            {onSelectTemplateCollection != null && (
              <FormGroup>
                <FieldLabel
                  isRequired={true}
                  label="Template collection "
                  labelStyle={FieldLabel.STYLES.LABEL}
                />
                <TemplateCollectionSelector
                  value={selectedTemplateCollectionId}
                  onChange={(template) => {
                    onSelectTemplateCollection(template.id);
                  }}
                  searchable={true}
                />
              </FormGroup>
            )}
            <FormGroup>
              <FieldLabel isRequired={true} label="Plate size " labelStyle={FieldLabel.STYLES.LABEL} />
              <PlateSchemaSizeSelector plateSize={plateSize} onChangePlateSize={onChangePlateSize} />
            </FormGroup>
            <FormGroup>
              <FieldLabel label="Schema" labelStyle={FieldLabel.STYLES.LABEL} />
              <PlateSchemaSelector
                plateSchemaType={PlateSchemaType.FIXED_PLATE}
                plateSchemaFilter={plateSchemaFilter}
                onChange={onChangePlateSchema}
                registryId={undefined}
                selectedPlateSchemaId={plateSchema?.id ?? null}
                disabled={plateSize == null}
                disabledMessage={plateSchemaSelectorDisabledMessage}
                shouldShowNoSchemaOption={true}
              />
            </FormGroup>
          </>
        )}
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(() => ({
  segmentLabelText: {
    display: 'inline',
    ...FONT_PROXIMA_12,
  },
  segmentLabelTitle: {
    ...FONT_PROXIMA_TITLE,
  },
  segmentLabel: {
    whiteSpace: 'normal',
    height: '100%',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  segmentControl: {
    height: 'auto',
    alignSelf: 'stretch',
  },
}));
